import { useMemo, useRef } from 'react';
import styled from '@emotion/styled';
import { Table, TableProps } from 'antd';
import { useSize } from 'ahooks';

export interface ScrollTableProps<T extends object> extends TableProps<T> {
  restHeight?: number;
}

const Wrapper = styled.div`
  height: 100%;
  overflow: hidden;
`;

const commonHeaderHeight = 55;
const paginationHeight = 65;

export const presetRestHeight = {
  common: commonHeaderHeight,
  withPagination: commonHeaderHeight + paginationHeight,
};

function ScrollTable<T extends object>(props: ScrollTableProps<T>) {
  const {
    restHeight: restHeightProp,
    pagination,
    className,
    style,
    scroll: scrollProp,
    ...otherProps
  } = props;

  const wrapperRef = useRef<HTMLDivElement>(null);

  const wrapperSize = useSize(wrapperRef);
  const height = wrapperSize?.height ?? 0;

  const theadSize = useSize(wrapperRef.current?.querySelector('thead') || null);
  const theadHeight = theadSize?.height ?? 0;

  const withPagination = Boolean(pagination);

  const scroll = useMemo<Required<TableProps<T>>['scroll']>(() => {
    const restHeight =
      restHeightProp ||
      (theadHeight ? theadHeight + (withPagination ? paginationHeight : 0) : theadHeight) ||
      (withPagination ? presetRestHeight.withPagination : presetRestHeight.common);

    return {
      ...scrollProp,
      y: height ? height - restHeight : 0,
    };
  }, [height, restHeightProp, theadHeight, withPagination, scrollProp]);

  return (
    <Wrapper ref={wrapperRef} className={className} style={style}>
      <Table scroll={scroll} pagination={pagination} {...otherProps} />
    </Wrapper>
  );
}

export default ScrollTable;
